// You: the numbers Musette plans with, made yours to see and change. Your rules (each a handle,
// previewed before it applies), your engine, your bikes and kit, your goals, your records,
// weight, and the settings that used to sit in the menu.
import { haptic, hapticsOn, setHaptics } from "../lib/haptic";
import { useEffect, useMemo, useState } from "preact/hooks";
import { useMusette, useSync, setSettings, moveRide, bikes as loadBikes, pushState, pushOn, setupStatus, type PushState } from "../lib/store";
import { getTheme, setTheme, type Theme } from "../lib/theme";
import { todayIso, addDays, dayLong, dateLabel, daysBetween, mondayOf, shortDate, weeksTo } from "../lib/dates";
import { toMin, hhmm } from "../lib/sun";
import { wallsFor, clampStart, WORK } from "../lib/day";
import { fitsLight, speedKmh } from "../lib/ride";
import { longest, biggestClimb, real, weeklyKm } from "../lib/analysis";
import { fitness } from "../lib/fitness";
import { daily, avg7, fmtW, type WeighIn } from "../lib/weight";
import { KIT_T, kitFor } from "../lib/weather";
import { Head } from "../components/Head";
import { useToast } from "../components/Sheet";
import type { State } from "../lib/types";
import { homeOf } from "../data/empty";

type Rules = State["settings"];
const DAYS = ["S", "M", "T", "W", "T", "F", "S"];
const DAYN = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const step = (t: string, by: number) => hhmm(Math.max(0, Math.min(24 * 60 - 15, toMin(t) + by)));

function Stepper({ label, value, onDown, onUp, sub }: { label: string; value: string; onDown: () => void; onUp: () => void; sub?: string }) {
  return (
    <div class="stepper">
      <span class="stepper-text"><span class="note-title">{label}</span>{sub && <span class="label">{sub}</span>}</span>
      <span class="stepper-ctl">
        <button type="button" onClick={onDown} aria-label={`${label}: less`}>−</button>
        <span class="stepper-v">{value}</span>
        <button type="button" onClick={onUp} aria-label={`${label}: more`}>+</button>
      </span>
    </div>
  );
}
function Toggle({ label, sub, on, onClick }: { label: string; sub?: string; on: boolean; onClick: () => void }) {
  return (
    <button type="button" class="toggle-row" role="switch" aria-checked={on} onClick={onClick}>
      <span class="stepper-text"><span class="note-title">{label}</span>{sub && <span class="label">{sub}</span>}</span>
      <span class={`switch${on ? " on" : ""}`} aria-hidden="true"><span /></span>
    </button>
  );
}

/** What a change of rules would move, among the rides of the next two weeks. */
function preview(s: State, draft: Rules, today: string) {
  const d2: State = { ...s, settings: draft };
  const out: { date: string; line: string; to?: { start: string; end: string }; booked: boolean }[] = [];
  for (const d of s.plan.flatMap((w) => w.days)) {
    if (d.date < today || d.date > addDays(today, 13) || d.type !== "ride" || d.status === "cancelled" || d.status === "done" || !d.start || !d.end) continue;
    const dur = toMin(d.end) - toMin(d.start);
    const name = dayLong(d.date);
    const booked = d.status === "booked";
    if (draft.keptEvenings.includes(new Date(`${d.date}T12:00:00Z`).getUTCDay()) && toMin(d.start) >= 17 * 60) { out.push({ date: d.date, line: `${name}'s ride lands on a kept evening`, booked }); continue; }
    const { start } = clampStart(toMin(d.start), dur, wallsFor(d2, d.date));
    const route = s.routes.find((r) => r.id === d.route);
    if (start !== toMin(d.start)) { out.push({ date: d.date, line: `${name}'s ride moves to ${hhmm(start)}`, to: { start: hhmm(start), end: hhmm(start + dur) }, booked }); continue; }
    if (route && toMin(d.start) >= 12 * 60 && !fitsLight(route, d.date, d.start, homeOf(s).lat, homeOf(s).lon, draft.latestFinish, draft.bufferMin, draft.sunsetRule !== false)) out.push({ date: d.date, line: `${name}: ${route.n} would finish in the dark. Swap it in Week`, booked });
  }
  return out;
}

export function You() {
  const s = useMusette();
  const sync = useSync();
  const today = todayIso();
  const { show, node: toast } = useToast();
  const [draft, setDraft] = useState<Rules>(s.settings);
  useEffect(() => { setDraft(s.settings); }, [JSON.stringify(s.settings)]);
  const work = draft.workHours ?? WORK;
  const kit = draft.kit ?? KIT_T;
  const changed = JSON.stringify(draft) !== JSON.stringify(s.settings);
  const moves = useMemo(() => (changed ? preview(s, draft, today) : []), [changed, JSON.stringify(draft), s.plan]);
  const set = (p: Partial<Rules>) => setDraft((d) => ({ ...d, ...p }));
  const setWork = (k: "thursday" | "weekday", i: 0 | 1, by: number) => { const w = structuredClone(work); w[k][i] = step(w[k][i], by); set({ workHours: w }); };
  const setKit = (i: 0 | 1 | 2, by: number) => { const k = [...kit] as [number, number, number]; k[i] += by; if (k[0] > k[1] && k[1] > k[2]) set({ kit: k }); };

  const apply = async () => {
    const was = s.settings;
    await setSettings(draft);
    let moved = 0;
    for (const m of moves) if (m.to && !m.booked) { await moveRide(m.date, m.to); moved++; }
    const held = moves.filter((m) => m.booked).length;
    show(moved ? `Saved. ${moved} ${moved === 1 ? "ride" : "rides"} moved${held ? `; ${held} booked, move ${held === 1 ? "it" : "them"} in Week` : ""}.` : "Saved.", () => setSettings(was));
  };

  // Engine
  const [mpk, setMpk] = useState(10);
  const f = useMemo(() => fitness(s, today), [s.rides, s.plan, s.ramp]);
  const [ftp, setFtp] = useState<string>(s.settings.ftp ? String(s.settings.ftp) : "");

  // Bikes
  const [gear, setGear] = useState<Awaited<ReturnType<typeof loadBikes>> | null>(null);
  useEffect(() => { if (sync.sync === "live") loadBikes().then(setGear); }, [sync.sync]);
  const retired = new Set(s.settings.retiredBikes ?? []);
  const [showRetired, setShowRetired] = useState(false);
  const retire = async (id: string, name: string, on: boolean) => {
    const next = on ? [...retired, id] : [...retired].filter((x) => x !== id);
    await setSettings({ retiredBikes: next, ...(on && s.settings.parisBike === id ? { parisBike: undefined } : {}) });
    show(on ? `${name} retired.` : `${name} is back.`, () => setSettings({ retiredBikes: [...retired] }));
  };
  const every = s.settings.chainEvery ?? 3000;

  // Records
  const rides = s.rides.filter(real);
  const top = longest(rides), climb = biggestClimb(rides);
  const year = rides.filter((r) => r.d >= "2026-01-01").reduce((a, r) => a + r.m, 0);
  const weeks = weeklyKm(rides, today, 52);
  const bestWeek = weeks.reduce((m, w) => (w.km > m.km ? w : m), { w: "", km: 0, rides: 0 });
  const since = "2026-10-26";
  const back = rides.filter((r) => r.d >= since);
  const firsts = [
    { title: "First ride of the plan", done: back.some((r) => r.km >= 15), next: "Any ride of 15 km or more" },
    { title: "First 100 km since restarting", done: back.some((r) => r.km >= 100), next: "Day 3 is 103 km" },
    { title: "First 150 km", done: rides.some((r) => r.km >= 150), next: "Day 2 is 146 km" },
    { title: "First back-to-back weekend", done: back.some((r) => new Date(`${r.d}T12:00:00Z`).getUTCDay() === 0 && back.some((x) => x.d === addDays(r.d, -1) && x.km >= 40) && r.km >= 40), next: "Saturday and Sunday, 40 km or more each" },
    { title: "First 1,500 m day", done: rides.some((r) => r.m >= 1500), next: "Day 1 climbs 1,553 m" },
  ];

  // Weight
  const [w, setW] = useState<WeighIn[] | null>(null);
  useEffect(() => { if (sync.sync === "live") fetch("/api/weight", { credentials: "same-origin" }).then((r) => (r.ok ? r.json() : { weighins: [] })).then((b) => setW(b.weighins ?? [])).catch(() => setW([])); }, [sync.sync]);
  const a7 = w ? avg7(daily(w), today) : null;

  // Settings
  const [push, setPush] = useState<PushState>("unsupported");
  useEffect(() => { pushState().then(setPush).catch(() => setPush("unsupported")); }, []);
  const [theme, setT] = useState<Theme>(getTheme());
  const live = sync.sync === "live";
  const [hap, setHap] = useState(hapticsOn());
  const d1 = s.stages[0];

  const [setup, setSetup] = useState<Record<string, any> | null>(null);
  useEffect(() => { if (live) setupStatus().then(setSetup); }, [live]);
  const ago = (iso?: string) => { if (!iso) return ""; const m = Math.round((Date.now() - Date.parse(iso)) / 60000); return m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} days ago`; };
  const setupRows = setup ? [
    { name: "Coach write-ups and live answers", ok: setup.anthropic, yes: "On", todo: "Add ANTHROPIC_KEY" },
    { name: "Strava", ok: setup.strava, yes: "Connected", todo: "Connect below" },
    { name: "Rides the moment they land", ok: !!setup.stravaPush, yes: setup.stravaPush?.last ? `Last ride in ${ago(setup.stravaPush.last)}` : "On, waiting for a ride", todo: "Strava push not on yet" },
    { name: "Diary", ok: setup.google, yes: "Connected", todo: "Connect below" },
    { name: "Street maps", ok: setup.maps, yes: "On", todo: "Add CARTO_KEY" },
    { name: "intervals.icu", ok: setup.intervals, yes: setup.intervalsAt ? `Read ${ago(setup.intervalsAt)}` : "Keys in, first read on the hour", todo: "Add INTERVALS_KEY and INTERVALS_ATHLETE" },
    { name: "Notifications", ok: setup.devices > 0, yes: `${setup.devices} ${setup.devices === 1 ? "device" : "devices"}`, todo: "Turn on below, from the home-screen app" },
    { name: "The 15-minute timer", ok: setup.tick && Date.now() - Date.parse(setup.tick.at) < 45 * 60000, yes: `Ran ${ago(setup.tick?.at)}`, todo: setup.tick ? `Last ran ${ago(setup.tick.at)}` : "Not run since 29 October" },
    { name: "Daybook tile", ok: setup.daybook, yes: "Writing", todo: "Add DAYBOOK_KEY" },
    { name: "Shortcuts (weigh-ins, GPX)", ok: setup.shortcut, yes: "Key set", todo: "Add SHORTCUT_KEY" },
    { name: "Cloud coach", ok: setup.coach, yes: "Key set", todo: "Add COACH_KEY" },
    { name: "Nightly backup", ok: !!setup.backup, yes: `Last ${setup.backup?.d ?? ""}`, todo: "First one tonight at 03:00" },
  ] : [];
  const setupDone = setupRows.filter((r) => r.ok).length;
  return (
    <main class="screen signal you">
      <Head right="You" />

      <section class="stack" style="margin-top: var(--space-5)">
        <div class="you-head">
          <span class="avatar big" aria-hidden="true">·</span>
          <span class="you-name"><span class="say">You</span><span class="soft">{d1 ? `Your goal in ${weeksTo(today, d1.date)} weeks` : "No goal set"}</span></span>
        </div>
        <div class="stats">
          <div class="card stat"><span class="label">Longest</span><span class="stat-n">{top?.km ?? 0}<small> km</small></span></div>
          <div class="card stat"><span class="label">Biggest climb</span><span class="stat-n">{climb?.m ?? 0}<small> m</small></span></div>
          <div class="card stat"><span class="label">2026 climbed</span><span class="stat-n">{year.toLocaleString("en-GB")}<small> m</small></span></div>
        </div>
        <span class="label">Measured by Strava, from the rides Musette holds.</span>
      </section>

      <section class="stack" style="margin-top: var(--space-6)">
        <span class="label ink sec">Your rules</span>
        <div class="card">
          <span class="label">Work</span>
          <Stepper label="Thursday, from home" sub="Starts" value={work.thursday[0]} onDown={() => setWork("thursday", 0, -15)} onUp={() => setWork("thursday", 0, 15)} />
          <Stepper label="Thursday" sub="Ends" value={work.thursday[1]} onDown={() => setWork("thursday", 1, -15)} onUp={() => setWork("thursday", 1, 15)} />
          <Stepper label="Other weekdays, door to door" sub="Leave" value={work.weekday[0]} onDown={() => setWork("weekday", 0, -15)} onUp={() => setWork("weekday", 0, 15)} />
          <Stepper label="Other weekdays" sub="Home" value={work.weekday[1]} onDown={() => setWork("weekday", 1, -15)} onUp={() => setWork("weekday", 1, 15)} />
        </div>
        <div class="card">
          <span class="label">Riding</span>
          <Stepper label="Finished by" sub="Every day" value={draft.latestFinish} onDown={() => set({ latestFinish: step(draft.latestFinish, -15) })} onUp={() => set({ latestFinish: step(draft.latestFinish, 15) })} />
          <Stepper label="Morning rides done by" sub="Weekdays, no buffer" value={draft.morningFinishBy} onDown={() => set({ morningFinishBy: step(draft.morningFinishBy, -15) })} onUp={() => set({ morningFinishBy: step(draft.morningFinishBy, 15) })} />
          <Stepper label="Buffer" sub="A puncture and stops" value={`${draft.bufferMin} min`} onDown={() => set({ bufferMin: Math.max(0, draft.bufferMin - 5) })} onUp={() => set({ bufferMin: Math.min(90, draft.bufferMin + 5) })} />
          <Toggle label="Back by sunset" sub="Weekday evenings; weekends may run dark" on={draft.sunsetRule !== false} onClick={() => set({ sunsetRule: draft.sunsetRule === false })} />
        </div>
        <div class="card">
          <div class="row-between"><span class="label">Kept evenings</span><span class="label">Never planned over</span></div>
          <div class="days7" role="group" aria-label="Kept evenings">
            {[1, 2, 3, 4, 5, 6, 0].map((n) => {
              const on = draft.keptEvenings.includes(n);
              return <button type="button" aria-pressed={on} aria-label={DAYN[n]} class={on ? "on" : ""} onClick={() => set({ keptEvenings: on ? draft.keptEvenings.filter((x) => x !== n) : [...draft.keptEvenings, n].sort() })}>{DAYS[n]}</button>;
            })}
          </div>
          <span class="soft">{draft.keptEvenings.length ? `${draft.keptEvenings.map((n) => DAYN[n]).join(" and ")} evenings are for Isabella.` : "No kept evenings."}</span>
        </div>
        <div class="card">
          <div class="row-between"><span class="label">Kit by feels-like</span><span class="label">At the start</span></div>
          <Stepper label="Shorts, short sleeves" sub="At or above" value={`${kit[0]}°`} onDown={() => setKit(0, -1)} onUp={() => setKit(0, 1)} />
          <Stepper label="Shorts, long sleeves" sub="At or above" value={`${kit[1]}°`} onDown={() => setKit(1, -1)} onUp={() => setKit(1, 1)} />
          <Stepper label="Tights, long sleeves" sub="At or above" value={`${kit[2]}°`} onDown={() => setKit(2, -1)} onUp={() => setKit(2, 1)} />
          <span class="soft">Below {kit[2]}°: {kitFor(kit[2] - 1, kit)}.</span>
        </div>
        <div class="card">
          <span class="label">Notifications</span>
          <span class="soft">Ride reminders, clashes, write-ups, milestones and answers to your questions always come. Quiet from 00:00 to 04:00.</span>
          <Stepper label="The coach speaking up" sub="Unprompted, a day at most" value={String(draft.coachPerDay ?? 3)} onDown={() => set({ coachPerDay: Math.max(0, (draft.coachPerDay ?? 3) - 1) })} onUp={() => set({ coachPerDay: Math.min(8, (draft.coachPerDay ?? 3) + 1) })} />
          <Toggle label="Wake calls in quiet hours" sub="For a ride before 04:45" on={draft.nightRide !== false} onClick={() => set({ nightRide: draft.nightRide === false })} />
        </div>
        <div class="card coach-style">
          <span class="label">How the coach talks</span>
          <span class="note-title">Training terms</span>
          <div class="segmented" role="radiogroup" aria-label="Training terms">
            {([["plain", "Explained"], ["full", "Coach-speak"]] as const).map(([k, l]) => <button type="button" role="radio" aria-checked={(s.settings.coachTerms ?? "plain") === k} aria-selected={(s.settings.coachTerms ?? "plain") === k} onClick={() => setSettings({ coachTerms: k })}>{l}</button>)}
          </div>
          <span class="soft">{(s.settings.coachTerms ?? "plain") === "plain" ? "Fitness, form and zones, each glossed the first time." : "CTL, TSB, Z2, sweet spot. No glosses."}</span>
          <span class="note-title">Speaking up</span>
          <div class="segmented" role="radiogroup" aria-label="When the coach speaks up">
            {([["matters", "When it matters"], ["daily", "Every morning"]] as const).map(([k, l]) => <button type="button" role="radio" aria-checked={(s.settings.coachCheckin ?? "matters") === k} aria-selected={(s.settings.coachCheckin ?? "matters") === k} onClick={() => setSettings({ coachCheckin: k })}>{l}</button>)}
          </div>
          <span class="soft">{(s.settings.coachCheckin ?? "matters") === "matters" ? "Clashes, weather calls and ride write-ups. Nothing else." : "Those, plus a line on the day ahead: 06:45 on weekdays, 08:00 at the weekend."}</span>
        </div>
      </section>

      <section class="stack" style="margin-top: var(--space-6)">
        <span class="label ink sec">Your engine</span>
        <div class="card">
          <div class="row-between"><span class="label ink">Pace model</span><span class="label">Fitted from seven 2026 rides</span></div>
          <div class="editor-time"><span class="hero-n sm">{speedKmh(1, mpk).toFixed(1)}</span><span class="editor-to"><span class="label">km/h</span><span class="stat-n">{mpk} m/km</span></span></div>
          <input type="range" class="range" min="0" max="20" step="0.5" value={mpk} onInput={(e) => setMpk(Number((e.target as HTMLInputElement).value))} aria-label="Metres of climb per kilometre" />
          <span class="soft">Moving speed falls 0.32 km/h for every metre of climb per kilometre. Werneth Low is 13.7 m/km; Day 1 is {d1?.km.v && d1?.gain.v ? (d1.gain.v / d1.km.v).toFixed(1) : "7.5"}. An estimate, within 0.8 km/h.</span>
        </div>
        <div class="card">
          <div class="row-between"><span class="label ink">Load per kilometre</span><span class="note-title">{f.perKm}</span></div>
          <span class="soft">How much Strava Relative Effort a kilometre carries for you, from your recent rides. It turns the planned rides into the fitness curve in Coach.</span>
        </div>
        <div class="card">
          <div class="row-between"><span class="label ink">FTP</span><span class="label">{s.settings.ftp ? `${s.settings.ftp} W` : "Not set"}</span></div>
          <div class="composer">
            <input type="number" inputMode="numeric" placeholder="Watts, once the power meter is in" value={ftp} onInput={(e) => setFtp((e.target as HTMLInputElement).value)} aria-label="FTP in watts" />
            <button type="button" class="send" disabled={!ftp || Number(ftp) < 80 || Number(ftp) > 600} onClick={async () => { await setSettings({ ftp: Number(ftp) }); show(`FTP ${ftp} W saved.`); }} aria-label="Save FTP">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12l5 5 9-10" /></svg>
            </button>
          </div>
        </div>
      </section>

      <section class="stack" style="margin-top: var(--space-6)">
        <span class="label ink sec">Bikes and kit</span>
        <div class="card" role="radiogroup" aria-label="The Paris bike">
          {live && gear && gear.bikes.length > 0 && <span class="soft">Tap a bike to make it the Paris bike.</span>}
          {!live && <span class="soft">Sign in to see your bikes from Strava.</span>}
          {live && gear?.needsPermission && <a class="menu-row" href="/api/strava/connect"><span>Reconnect Strava to show your bikes</span><span class="menu-detail">One tap</span></a>}
          {live && gear && !gear.needsPermission && !gear.bikes.length && <span class="soft">{gear.connected ? "No bikes on Strava." : "Connect Strava below to see your bikes."}</span>}
          {gear?.bikes.filter((b) => showRetired || !retired.has(b.id)).map((b) => {
            const paris = s.settings.parisBike === b.id;
            const gone = retired.has(b.id);
            const since = b.km - (s.settings.chain?.[b.id] ?? b.km);
            const chainSet = s.settings.chain?.[b.id] != null;
            return (
              <div class="bike">
                <button type="button" role="radio" aria-checked={paris} disabled={gone} class={`bike-pick${paris ? " on" : ""}`} onClick={async () => { await setSettings({ parisBike: paris ? undefined : b.id }); show(paris ? "No Paris bike set." : `${b.name} is the Paris bike.`); }}>
                  <span class="radio" aria-hidden="true" />
                  <span class="note-title">{b.name}</span>
                  <span class="label">{paris ? "Paris bike · " : ""}{b.km.toLocaleString("en-GB")} km</span>
                </button>
                {gone ? <button type="button" class="text-btn small quiet" onClick={() => retire(b.id, b.name, false)}>Retired · bring it back</button> : <>
                <div class="row-between bike-meta">
                  {chainSet
                    ? <span class={`label${since >= every ? " amber" : ""}`}>Chain {since.toLocaleString("en-GB")} of {every.toLocaleString("en-GB")} km</span>
                    : <button type="button" class="text-btn small quiet" onClick={async () => { await setSettings({ chain: { ...(s.settings.chain ?? {}), [b.id]: b.km } }); show(`Chain counted from today on the ${b.name}.`); }}>New chain today</button>}
                  {!paris && <button type="button" class="text-btn small quiet" onClick={() => retire(b.id, b.name, true)}>I don't own this any more</button>}
                </div>
                {chainSet && since >= every && <button type="button" class="text-btn small" onClick={async () => { await setSettings({ chain: { ...(s.settings.chain ?? {}), [b.id]: b.km } }); show("Chain reset."); }}>Changed it today</button>}
                </>}
              </div>
            );
          })}
          {gear && [...retired].some((id) => gear.bikes.some((b) => b.id === id)) && <button type="button" class="text-btn small quiet" onClick={() => setShowRetired(!showRetired)}>{showRetired ? "Hide retired bikes" : `Show ${gear.bikes.filter((b) => retired.has(b.id)).length} retired`}</button>}
        </div>
        <div class="card">
          <span class="label">On the bike</span>
          <div class="row-between"><span class="note-title">Wahoo Elemnt Roam 2</span><span class="label">On the ready list</span></div>
          <div class="row-between"><span class="note-title">Heart-rate strap</span><span class="label">Feeds the write-ups</span></div>
          <div class="row-between"><span class="note-title">Power meter</span><span class="label">On order</span></div>
        </div>
      </section>

      <section class="stack" style="margin-top: var(--space-6)">
        <span class="label ink sec">Goals</span>
        <div class="menu-card">
          <a class="menu-row" href="/paris"><span>Manchester to Paris</span><span class="menu-detail">12 to 15 June</span></a>
          <a class="menu-row" href="/mallorca"><span>Mallorca</span><span class="menu-detail">1 to 15 August</span></a>
        </div>
      </section>

      <section class="stack" style="margin-top: var(--space-6)">
        <span class="label ink sec">Records</span>
        <div class="card">
          {top && <div class="row-between"><span class="note-title">Longest ride</span><span class="label">{top.km} km · {dateLabel(top.d)}</span></div>}
          {climb && <div class="row-between"><span class="note-title">Biggest climb</span><span class="label">{climb.m} m · {dateLabel(climb.d)}</span></div>}
          {bestWeek.km > 0 && <div class="row-between"><span class="note-title">Best week</span><span class="label">{Math.round(bestWeek.km)} km · {dateLabel(bestWeek.w)}</span></div>}
          {f.peak && <div class="row-between"><span class="note-title">Peak fitness</span><span class="label">{Math.round(f.peak.ctl)} · {dateLabel(f.peak.d)}</span></div>}
        </div>
        <div class="card">
          <span class="label">Firsts on the way to Paris</span>
          {firsts.map((x) => (
            <div class={`first${x.done ? " done" : ""}`}>
              <span class="first-dot" aria-hidden="true" />
              <span class="stepper-text"><span class="note-title">{x.title}</span><span class="label">{x.done ? "Done" : x.next}</span></span>
            </div>
          ))}
        </div>
      </section>

      <section class="stack" style="margin-top: var(--space-6)">
        <span class="label ink sec">Weight</span>
        <div class="segmented" role="radiogroup" aria-label="Weight in">
          {(["st", "kg"] as const).map((u) => <button type="button" role="radio" aria-checked={(s.settings.weightUnit ?? "st") === u} aria-selected={(s.settings.weightUnit ?? "st") === u} onClick={() => setSettings({ weightUnit: u })}>{u === "st" ? "Stones and pounds" : "Kilograms"}</button>)}
        </div>
        <a class="card row-between" href="/weight"><span class="note-title">Seven-day average</span><span class="label">{a7 != null ? fmtW(a7, s.settings.weightUnit ?? "st") : w && w.length ? `${fmtW(w[w.length - 1].kg, s.settings.weightUnit ?? "st")} · last weigh-in` : "From the scales, once the Shortcut runs"}</span></a>
      </section>

      {live && setup && (
        <section class="stack" style="margin-top: var(--space-6)">
          <div class="row-between"><span class="label ink sec">Setup</span><span class="label">{setupDone} of {setupRows.length} done</span></div>
          <div class="menu-card">
            {setupRows.map((r) => (
              <div class="menu-row setup-row">
                <span class="setup-name"><span class={`setup-dot${r.ok ? " ok" : ""}`} aria-hidden="true" />{r.name}</span>
                <span class="menu-detail">{r.ok ? r.yes : r.todo}</span>
              </div>
            ))}
          </div>
          <span class="label">Keys go into Cloudflare by you: Workers &amp; Pages, musette, Settings, Variables and secrets. Musette never sees or shows a key, only whether it is there.</span>
        </section>
      )}

      <section class="stack" style="margin-top: var(--space-6)">
        <span class="label ink sec">Settings</span>
        <div class="menu-card">
          {(sync.sync === "signed-out" || sync.sync === "seed") && <a class="menu-row" href="/signin"><span>Sign in to your data</span><span class="menu-detail">Key</span></a>}
          {live && sync.google?.configured && <a class="menu-row" href={sync.google.connected ? undefined : "/api/google/connect"}><span>Diary</span><span class="menu-detail">{sync.google.connected ? "Connected" : "Connect"}</span></a>}
          {live && sync.strava?.configured && <a class="menu-row" href={sync.strava.connected && !gear?.needsPermission ? undefined : "/api/strava/connect"}><span>Strava</span><span class="menu-detail">{sync.strava.connected ? (gear?.needsPermission ? "Reconnect for bikes" : "Connected") : "Connect"}</span></a>}
          {live && push === "off" && <button type="button" class="menu-row" onClick={async () => { show(await pushOn()); setPush(await pushState()); }}><span>Notifications</span><span class="menu-detail">Turn on</span></button>}
          {push === "on" && <div class="menu-row"><span>Notifications</span><span class="menu-detail">On</span></div>}
          {push === "home-screen" && <div class="menu-row"><span>Notifications</span><span class="menu-detail">From the home-screen app</span></div>}
          {push === "blocked" && <div class="menu-row"><span>Notifications</span><span class="menu-detail">Blocked in Settings</span></div>}
          <button type="button" class="menu-row" onClick={() => setSettings({ band: s.settings.band === "full" ? "fold" : "full" })}><span>Dragging a ride</span><span class="menu-detail">{s.settings.band === "full" ? "Full day to scale" : "Blocked time folded"}</span></button>
          <button type="button" class="menu-row" role="switch" aria-checked={hap} onClick={() => { const on = !hap; setHaptics(on); setHap(on); if (on) haptic(); }}><span>Haptics</span><span class="menu-detail">{hap ? "On" : "Off"}</span></button>
          <a class="menu-row" href="/privacy"><span>Privacy</span><span class="menu-detail" /></a>
        </div>
        <div class="segmented three appearance" role="radiogroup" aria-label="Appearance">
          {(["system", "light", "dark"] as Theme[]).map((t) => (
            <button type="button" role="radio" aria-checked={theme === t} aria-selected={theme === t} onClick={() => { setTheme(t); setT(t); }}>{t === "system" ? "Automatic" : t === "light" ? "Day" : "Night"}</button>
          ))}
        </div>
        {sync.strava?.last && <span class="label">Strava synced {new Date(sync.strava.last).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" })}{sync.sync === "offline" ? " · No signal: the last copy is showing" : ""}</span>}
      </section>

      <div class="dock-wrap">
        {toast}
        {changed && (
          <div class="dock">
            {moves.length > 0 && <div class="preview">{moves.slice(0, 3).map((m) => <span class="soft">{m.line}{m.booked ? " (booked: move it in Week)" : ""}.</span>)}</div>}
            <div class="row-between" style="gap: var(--space-2)">
              <button type="button" class="pill ghost" onClick={() => setDraft(s.settings)}>Discard</button>
              <button type="button" class="pill wide" onClick={apply}>{moves.find((m) => m.to && !m.booked) ? `Apply: ${moves.find((m) => m.to && !m.booked)!.line.replace(/'s ride/, "")}` : "Save rules"}</button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
